import { useState, useEffect } from "react";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { 
  History, 
  Plus, 
  Pencil, 
  Trash2, 
  ArrowRight,
  RefreshCw,
  Clock,
  X
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_ICONS = {
  created: { icon: Plus, color: "text-emerald-500", bg: "bg-emerald-500/10" },
  updated: { icon: Pencil, color: "text-blue-500", bg: "bg-blue-500/10" },
  status_changed: { icon: RefreshCw, color: "text-amber-500", bg: "bg-amber-500/10" },
  deleted: { icon: Trash2, color: "text-red-500", bg: "bg-red-500/10" }
};

const ACTION_LABELS = {
  created: "Creato",
  updated: "Modificato",
  status_changed: "Status cambiato",
  deleted: "Eliminato"
};

const FIELD_LABELS = {
  info: "Descrizione",
  system: "Sistema",
  channel: "Channel",
  macro: "Macro",
  country: "Paese",
  priority: "Priorità",
  status: "Status",
  deadline: "Deadline",
  completion: "Completamento",
  project: "Progetto"
};

export default function TaskHistoryModal({ open, onClose, taskId, taskInfo }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && taskId) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tasks/${taskId}/history`);
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatValue = (field, value) => {
    if (value === null || value === undefined) return "—";
    if (field === 'completion') return `${Math.round(value * 100)}%`;
    if (field === 'deadline' && value) {
      try {
        return new Date(value).toLocaleDateString('it-IT');
      } catch {
        return value;
      }
    }
    return String(value);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="task-history-modal-container">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/80 animate-in fade-in-0"
        onClick={onClose}
      />
      
      {/* Content */}
      <div className="fixed left-[50%] top-[50%] z-50 w-[calc(100%-2rem)] sm:max-w-[500px] max-h-[90vh] overflow-hidden translate-x-[-50%] translate-y-[-50%] bg-card border border-border p-6 shadow-lg rounded-lg animate-in fade-in-0 zoom-in-95" data-testid="task-history-modal">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none"
        >
          <X className="h-4 w-4" />
        </button>
        
        <div className="flex flex-col space-y-1.5 text-center sm:text-left mb-4">
          <h2 className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Storico Modifiche
          </h2>
          {taskInfo && (
            <p className="text-sm text-muted-foreground truncate mt-1">{taskInfo}</p>
          )}
        </div>
        
        <ScrollArea className="h-[400px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
              <Clock className="w-8 h-8 mb-2" />
              <p>Nessuna modifica registrata</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry) => {
                const actionConfig = ACTION_ICONS[entry.action] || ACTION_ICONS.updated;
                const Icon = actionConfig.icon;
                
                return (
                  <div 
                    key={entry.id} 
                    className="relative pl-8 pb-4 border-l-2 border-border last:border-l-0"
                    data-testid={`history-entry-${entry.id}`}
                  >
                    {/* Timeline dot */}
                    <div className={`absolute -left-3 top-0 w-6 h-6 rounded-full ${actionConfig.bg} flex items-center justify-center`}>
                      <Icon className={`w-3 h-3 ${actionConfig.color}`} />
                    </div>
                    
                    {/* Content */}
                    <div className="bg-secondary/30 rounded-lg p-3 ml-2">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline" className={`${actionConfig.bg} ${actionConfig.color} border-0`}>
                          {ACTION_LABELS[entry.action]}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(entry.timestamp)}
                        </span>
                      </div>
                      
                      {entry.action === "created" && (
                        <p className="text-sm text-muted-foreground">Task creato</p>
                      )}
                      
                      {entry.action === "deleted" && (
                        <p className="text-sm text-muted-foreground">Task eliminato</p>
                      )}
                      
                      {(entry.action === "updated" || entry.action === "status_changed") && entry.changes && (
                        <div className="space-y-2">
                          {Object.entries(entry.changes).map(([field, change]) => (
                            <div key={field} className="text-sm">
                              <span className="text-muted-foreground">
                                {FIELD_LABELS[field] || field}:
                              </span>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="px-2 py-0.5 bg-red-500/10 text-red-400 rounded text-xs">
                                  {formatValue(field, change.from)}
                                </span>
                                <ArrowRight className="w-3 h-3 text-muted-foreground" />
                                <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded text-xs">
                                  {formatValue(field, change.to)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        di {entry.user}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
